'use client';

import React, { useState, useEffect } from 'react';
import { Callout, Button, Title, Text, Card } from '@tremor/react';
import { RiAlarmWarningLine } from '@remixicon/react';
import axios from 'axios';
import { useRouter } from 'next/navigation';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';

interface LeadStatus {
    id: string;
    profileReturning2025: boolean | null;
    profileReturnYear2025: number | null;
    formProgress: string | null;
    investmentAmount2025?: number | null;
}

interface ReturningInvestorWizardProps {
    userId: string;
    initialLeadStatus: LeadStatus | null;
}

export default function ReturningInvestorWizard({ userId, initialLeadStatus }: ReturningInvestorWizardProps) {
    const router = useRouter();
    const { toast } = useToast();
    
    // Wizard state - Start with all fields visible
    const [wizardStep, setWizardStep] = useState<number>(1);
    // Default to "yes" for returning investor
    const [isReturningSelection, setIsReturningSelection] = useState<'yes' | 'no' | null>(
        initialLeadStatus?.profileReturning2025 === false ? 'no' : 'yes'
    );
    // Default to 2nd year if not specified
    const [returnYearSelection, setReturnYearSelection] = useState<2 | 3 | null>(
        initialLeadStatus?.profileReturnYear2025 === 3 ? 3 : 2
    );
    const [isSavingWizard, setIsSavingWizard] = useState<boolean>(false);
    // Start with null instead of 0
    const [investmentAmount2025, setInvestmentAmount2025] = useState<number | null>(
        initialLeadStatus?.investmentAmount2025 || null
    );

    useEffect(() => {
        // Set initial wizard step based on existing status
        if (initialLeadStatus) {
            if (initialLeadStatus.profileReturning2025 === false) {
                setWizardStep(99);
            } else if (initialLeadStatus.profileReturnYear2025 && initialLeadStatus.investmentAmount2025 !== null && initialLeadStatus.investmentAmount2025 !== undefined) {
                // If they've already completed everything, go to step 3
                setWizardStep(3);
            }
        }
    }, [initialLeadStatus]);

    const handleSaveAllData = async () => {
        const leadIdToUpdate = initialLeadStatus?.id;
        if (!leadIdToUpdate) {
            toast({
                title: "Error",
                description: "Cannot save status: Lead ID missing.",
                variant: "destructive"
            });
            return;
        }

        if (isReturningSelection === 'yes' && (investmentAmount2025 === null || investmentAmount2025 === undefined || investmentAmount2025 < 0)) {
            toast({
                title: "Error",
                description: "Please enter your 2025 investment amount.",
                variant: "destructive"
            });
            return;
        }

        setIsSavingWizard(true);
        try {
            // Save returning status
            await axios.put(`/api/crm/leads/${leadIdToUpdate}/returning-status`, {
                isReturning: isReturningSelection === 'yes',
                returnYear: isReturningSelection === 'yes' ? returnYearSelection : null
            });

            // Save investment amount if returning
            if (isReturningSelection === 'yes' && investmentAmount2025 !== null && investmentAmount2025 >= 0) {
                await axios.put(`/api/crm/leads/${leadIdToUpdate}/investment-amount`, {
                    investmentAmount2025: investmentAmount2025
                });
            }

            toast({
                title: "Success",
                description: "Your information has been saved.",
                variant: "default"
            });

            // Move to next step or confirmation
            if (isReturningSelection === 'yes') {
                setWizardStep(3);
            } else {
                setWizardStep(99);
            }

            // Trigger notification email for returning investors
            if (isReturningSelection === 'yes') {
                try {
                    await axios.post('/api/notify/returning-investor', {
                        leadId: leadIdToUpdate,
                        isReturning: true
                    });
                } catch (notifyError) {
                    console.error("Error sending returning investor notification:", notifyError);
                }
            }
        } catch (error) {
            console.error("Error saving data:", error);
            toast({
                title: "Error",
                description: "Could not save your information.",
                variant: "destructive"
            });
        } finally {
            setIsSavingWizard(false);
        }
    };

    const formatCurrency = (amount: number | null) => {
        if (amount === null || amount === undefined) {
            return '';
        }
        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    };

    return (
        <div className="p-6 border-l border-gray-200">
            <div className="space-y-6">
                <Title>Returning Investor Check</Title>
                
                {/* Wizard Step 1 - Show all fields at once */}
                {wizardStep === 1 && (
                    <>
                        <Callout 
                            title="🚨 ACTION REQUIRED - 2025 Investment Information" 
                            icon={RiAlarmWarningLine} 
                            color="red"
                        >
                            <Text className="mt-2 font-semibold">
                                Please complete all fields below to continue with your 2025 investment process.
                            </Text>
                        </Callout>

                        <div className="space-y-4">
                            {/* Question 1: Returning Investor */}
                            <Card className="border-2 border-red-200">
                                <div className="p-4">
                                    <Text className="font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                                        1. Are you a returning investor intending to participate in the 2025 financing season?
                                    </Text>
                                    <div className="flex mt-3 space-x-3">
                                        <Button
                                            size="sm"
                                            variant={isReturningSelection === 'yes' ? 'primary' : 'secondary'}
                                            className={isReturningSelection === 'yes' ? 'bg-green-600 hover:bg-green-700' : ''}
                                            onClick={() => setIsReturningSelection('yes')}
                                            disabled={isSavingWizard}
                                        >
                                            Yes, I&apos;m Returning
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant={isReturningSelection === 'no' ? 'primary' : 'secondary'}
                                            onClick={() => setIsReturningSelection('no')}
                                            disabled={isSavingWizard}
                                        >
                                            No, I&apos;m Not Returning
                                        </Button>
                                    </div>
                                </div>
                            </Card>

                            {/* Question 2: Investment Year - Show if returning */}
                            {isReturningSelection === 'yes' && (
                                <Card className="border-2 border-blue-200">
                                    <div className="p-4">
                                        <Text className="font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                                            2. Is this your second or third year investing with TPC?
                                        </Text>
                                        <div className="flex mt-3 space-x-3">
                                            <Button
                                                size="sm"
                                                variant={returnYearSelection === 2 ? 'primary' : 'secondary'}
                                                className={returnYearSelection === 2 ? 'bg-blue-600 hover:bg-blue-700' : ''}
                                                onClick={() => setReturnYearSelection(2)}
                                                disabled={isSavingWizard}
                                            >
                                                2nd Year
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={returnYearSelection === 3 ? 'primary' : 'secondary'}
                                                className={returnYearSelection === 3 ? 'bg-blue-600 hover:bg-blue-700' : ''}
                                                onClick={() => setReturnYearSelection(3)}
                                                disabled={isSavingWizard}
                                            >
                                                3rd Year
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {/* Question 3: Investment Amount - Show if returning */}
                            {isReturningSelection === 'yes' && (
                                <Card className="border-4 border-purple-300 bg-purple-50">
                                    <div className="p-6">
                                        <Text className="text-lg font-bold text-purple-900">
                                            3. 2025 Investment Amount
                                        </Text>
                                        <Text className="mt-2 text-purple-800">
                                            Please specify your intended investment amount for the 2025 financing season:
                                        </Text>
                                        <div className="flex items-center mt-4 space-x-2">
                                            <div className="relative flex-1 max-w-sm">
                                                <span className="absolute inset-y-0 flex items-center text-purple-600 left-3 text-lg font-semibold">$</span>
                                                <Input
                                                    type="number"
                                                    value={investmentAmount2025 ?? ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setInvestmentAmount2025(value === '' ? null : parseInt(value) || 0);
                                                    }}
                                                    className="pl-8 pr-3 text-lg border-2 border-purple-300 focus:border-purple-500"
                                                    placeholder="Enter amount"
                                                    min="0"
                                                    disabled={isSavingWizard}
                                                />
                                            </div>
                                        </div>
                                        {(initialLeadStatus?.investmentAmount2025 !== undefined && 
                                         initialLeadStatus?.investmentAmount2025 !== null && 
                                         initialLeadStatus.investmentAmount2025 > 0) && (
                                            <Text className="mt-2 text-sm text-gray-600">
                                                Current saved amount: {formatCurrency(initialLeadStatus.investmentAmount2025)}
                                            </Text>
                                        )}
                                    </div>
                                </Card>
                            )}

                            {/* Submit Button */}
                            <div className="flex justify-end mt-6">
                                <Button
                                    size="lg"
                                    className="px-8"
                                    onClick={handleSaveAllData}
                                    loading={isSavingWizard}
                                    disabled={isSavingWizard || (isReturningSelection === 'yes' && (investmentAmount2025 === null || investmentAmount2025 === undefined))}
                                >
                                    {isReturningSelection === 'no' ? 'Submit - Not Returning' : 'Continue to Next Step'}
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {/* Wizard Step 3 */}
                {wizardStep === 3 && (
                    <>
                        <Title>Next Steps for Returning Investors</Title>

                        {/* 1. Update Profile Section */}
                        <div className="p-4 mt-4 border border-blue-200 rounded-md bg-blue-50">
                            <Text className="font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                                1. Update Your Profile
                            </Text>
                            <Text className="mt-1">
                                Please review and update your investor profile information if anything has changed since last year.
                            </Text>
                            <div className="mt-3">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => router.push(initialLeadStatus?.id ? `/leads/${initialLeadStatus.id}` : '/leads')}
                                >
                                    Go to Profile
                                </Button>
                            </div>
                        </div>

                        {/* 2. Upload Documents Section */}
                        <div className="p-4 mt-6 border border-green-200 rounded-md bg-green-50">
                            <Text className="font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                                2. Upload 2025 Documents
                            </Text>
                            <Text className="mt-1">
                                Please upload the following documents for the 2025 season:
                            </Text>
                            <ul className="pl-5 mt-2 space-y-1 list-disc text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                                <li>2024 tax return & K1s</li>
                                <li>Personal Financial statement that is not older than 3 months</li>
                            </ul>
                            <div className="mt-3">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => router.push(initialLeadStatus?.id ? `/leads/${initialLeadStatus.id}` : '/leads')}
                                >
                                    Go to Document Uploads
                                </Button>
                            </div>
                        </div>

                        {/* 3. Investment Amount Section */}
                        <div className="p-4 mt-6 border border-purple-200 rounded-md bg-purple-50">
                            <Text className="font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                                3. Update 2025 Investment Amount
                            </Text>
                            <Text className="mt-1">
                                Please specify your intended investment amount for the 2025 financing season:
                            </Text>
                            <div className="flex items-center mt-3 space-x-2">
                                <div className="relative flex-1">
                                    <span className="absolute inset-y-0 flex items-center text-gray-500 left-3">$</span>
                                    <Input
                                        type="number"
                                        value={investmentAmount2025 ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setInvestmentAmount2025(value === '' ? null : parseInt(value) || 0);
                                        }}
                                        className="pl-8 pr-3"
                                        placeholder="Enter amount"
                                        min="0"
                                        disabled={isSavingWizard}
                                    />
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => handleSaveAllData()}
                                    loading={isSavingWizard}
                                    disabled={isSavingWizard || investmentAmount2025 === undefined || investmentAmount2025 === null || investmentAmount2025 < 0}
                                >
                                    Save
                                </Button>
                            </div>
                            {(initialLeadStatus?.investmentAmount2025 !== undefined && 
                             initialLeadStatus?.investmentAmount2025 !== null && 
                             initialLeadStatus.investmentAmount2025 > 0) && (
                                <Text className="mt-2 text-sm text-gray-600">
                                    Current saved amount: {formatCurrency(initialLeadStatus.investmentAmount2025)}
                                </Text>
                            )}
                        </div>
                    </>
                )}

                {/* Wizard Step 99 */}
                {wizardStep === 99 && (
                    <>
                        <Title>Confirmation</Title>
                        <Text className="mt-2">
                            Thank you for letting us know. If you change your mind or have questions, please contact support.
                        </Text>
                    </>
                )}
            </div>
        </div>
    );
} 